import { InjectionToken } from '@angular/core';

/**
 * Auth Configuration Interface
 */
export interface AuthConfig {

  // API Endpoints
  apiBaseUrl: string;
  loginEndpoint: string;
  logoutEndpoint: string;
  refreshTokenEndpoint: string;
  registerEndpoint: string;
  forgotPasswordEndpoint: string;
  resetPasswordEndpoint: string;
  changePasswordEndpoint: string;
  userProfileEndpoint: string;

  // Storage Keys
  accessTokenKey: string;
  refreshTokenKey: string;
  userKey: string;
  sessionIdKey: string;
  accountIdKey: string;

  // Token Settings
  tokenPrefix: string;
  headerName: string;

  // Redirect URLs
  loginRedirectUrl: string;
  logoutRedirectUrl: string;
  unauthorizedRedirectUrl: string;

  // Token Refresh Settings
  refreshTokenBeforeExpiry: number;
  enableAutoRefresh: boolean;

  // URL Rules
  whitelistedUrls: string[];
  blacklistedUrls: string[];

  // Refresh Token Cookie Settings
  useHttpOnlyCookie: boolean;
  cookieDomain: string;
  cookiePath: string;
  cookieSecure: boolean;
  cookieSameSite: 'Strict' | 'Lax' | 'None';
  cookieExpireDays: number;
}

/**
 * Default Auth Config
 */
export const DEFAULT_AUTH_CONFIG: AuthConfig = {

  // API Endpoints
  apiBaseUrl: '',
  loginEndpoint: '/login',
  logoutEndpoint: '/logout',
  refreshTokenEndpoint: '/refresh-token',
  registerEndpoint: '/register',
  forgotPasswordEndpoint: '/forgot-password',
  resetPasswordEndpoint: '/reset-password',
  changePasswordEndpoint: '/change-password',
  userProfileEndpoint: '/user/profile',

  // Storage Keys
  accessTokenKey: 'access_token',
  refreshTokenKey: 'refresh_token',
  userKey: 'user',
  sessionIdKey: 'session_id',
  accountIdKey: 'account_id',

  // Token Settings
  tokenPrefix: 'Bearer',
  headerName: 'Authorization',

  // Redirect URLs
  loginRedirectUrl: '/dashboard',
  logoutRedirectUrl: '/login',
  unauthorizedRedirectUrl: '/login',

  // Refresh Settings
  refreshTokenBeforeExpiry: 300, // 5 minutes
  enableAutoRefresh: true,

  // URL Rules
  whitelistedUrls: [
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/public'
  ],
  blacklistedUrls: [],

  // Cookie Settings
  useHttpOnlyCookie: true,
  cookieDomain: '',
  cookiePath: '/',
  cookieSecure: true, // false for localhost
  cookieSameSite: 'Strict',
  cookieExpireDays: 7
};

/**
 * Injection Token
 */
export const AUTH_CONFIG = new InjectionToken<AuthConfig>('AUTH_CONFIG');

/**
 * Helper to merge config with defaults
 */
export function provideAuthConfig(
  config: Partial<AuthConfig>
): AuthConfig {
  return { ...DEFAULT_AUTH_CONFIG, ...config };
}
